<!--
  PlatformFeeRate -- SuperAdmin editor for the platform-wide ThreadFlow
  platform fee rate.

  _No card -- feature approved by Jake in-session (SA settings UI for the
  platform fee rate). Flagged for Boulders triage.

  Functional model:
    * The ThreadFlow platform fee applied to the cart total (excluding
      shipping). Seeded at 0.0395 (3.95%).
    * NULL here means "no platform fee configured" -- the pricing
      pipeline degrades to no platform fee.

  Lives on /admin/plans-pricing (PlansPricing.vue), positioned AFTER the
  PlatformDefaultTimezone card and ABOVE the existing mock plans table.
  Carries its own local "Live" indicator so reviewers know this card is
  real even when the rest of the page is DEMO DATA. Placement is
  functional-now; the IA/layout of this page is slated for a later
  polish pass once the quantity/stitch-count pricing tiers land.

  Backend (shipped in #2254):
    * GET   /api/platform-settings/platform-fee-rate
        -> {"value": <fraction or null>}
        @login_required only (non-sensitive; any logged-in user reads)
    * PATCH /api/platform-settings/platform-fee-rate
        body {"value": <fraction or null>}
        gated on ``superadmin.settings`` (SA-only; 403 for non-SA; 400
        on bad input -- rejects bool/string/NaN/Inf/out-of-range [0,1])

  Shape mirrors TFBlankCostMargin.vue exactly -- same percent input +
  Save + Reset pattern, same axios mount/save flow, same v-progress-
  circular loading affordance, same 2dp percent formatting.
  data-testid prefix: platform-fee-rate-*.
-->
<template>
    <section
        class="platform-fee-rate"
        data-testid="platform-fee-rate-root"
    >
        <div class="platform-fee-rate__header">
            <h2 class="platform-fee-rate__title">Platform fee</h2>
            <span
                class="platform-fee-rate__live-badge"
                data-testid="platform-fee-rate-live"
                title="This section is backed by the live platform_settings endpoint -- not mock data."
            >
                Live
            </span>
        </div>

        <p class="platform-fee-rate__lede">
            ThreadFlow platform fee applied to the cart total (excluding
            shipping). Setting this back to blank clears the value; the
            pricing pipeline then applies no platform fee.
        </p>

        <v-progress-circular
            v-if="loading"
            :indeterminate="true"
            color="primary"
            size="32"
            width="4"
            class="mt-4"
            data-testid="platform-fee-rate-loading"
        />

        <div v-else class="platform-fee-rate__form">
            <label
                class="platform-fee-rate__label"
                for="platform-fee-rate-input"
            >
                Platform fee
            </label>
            <div class="platform-fee-rate__input-row">
                <input
                    id="platform-fee-rate-input"
                    v-model="percentDraft"
                    type="number"
                    inputmode="decimal"
                    step="0.01"
                    min="0"
                    max="100"
                    placeholder="3.95"
                    class="platform-fee-rate__input"
                    data-testid="platform-fee-rate-input"
                    @input="onInput"
                />
                <span class="platform-fee-rate__suffix">%</span>
            </div>
            <p
                v-if="errorMsg"
                class="platform-fee-rate__error"
                data-testid="platform-fee-rate-error"
            >
                {{ errorMsg }}
            </p>
            <p
                v-else
                class="platform-fee-rate__hint"
                data-testid="platform-fee-rate-hint"
            >
                {{ hint }}
            </p>

            <div class="platform-fee-rate__actions">
                <button
                    type="button"
                    class="tf-btn tf-btn--primary"
                    :disabled="!canSave"
                    data-testid="platform-fee-rate-save"
                    @click="save"
                >
                    {{ saving ? "Saving..." : "Save" }}
                </button>
                <button
                    v-if="hasStoredValue"
                    type="button"
                    class="tf-btn tf-btn--ghost"
                    :disabled="saving"
                    data-testid="platform-fee-rate-reset"
                    @click="resetToCleared"
                >
                    Clear platform fee
                </button>
            </div>
        </div>
    </section>
</template>

<script>
import { computed, ref, onMounted } from "vue";
import axios from "@/axios";

const URL = "/api/platform-settings/platform-fee-rate";

// Display precision for the percent field. The stored value is a
// fraction in [0, 1]; converting to a percent via ``fraction * 100``
// introduces IEEE-754 float artifacts (e.g. 0.0395 * 100 ===
// 3.9500000000000006). Rounding to 2 decimal places preserves real
// decimals an operator would enter (3.95%) while killing those
// artifacts. 2dp is the MVP default for a percent field.
const PERCENT_DISPLAY_DECIMALS = 2;

// Convert a stored fraction (e.g. 0.0395) to a clean percent string for
// DISPLAY ("3.95"). Number(...toFixed(2)) drops trailing zeros so 0.04
// renders "4" rather than "4.00". This formats only the value coming
// FROM the backend -- it never touches what the user types into the
// input.
function formatPercent(fraction) {
    if (fraction === null || fraction === undefined) return "";
    const pct = Number((fraction * 100).toFixed(PERCENT_DISPLAY_DECIMALS));
    return String(pct);
}

export default {
    name: "PlatformFeeRate",

    // Reports the stored fraction (or null) after every load/save so the
    // page can reuse it without a second fetch.
    emits: ["rate-change"],

    setup(_props, { emit }) {
        // Stored fraction in [0, 1] from the backend; null = "no
        // platform fee configured".
        const storedFraction = ref(null);
        const percentDraft = ref("");
        const loading = ref(true);
        const saving = ref(false);
        const errorMsg = ref("");

        const hasStoredValue = computed(
            () => storedFraction.value !== null
        );

        const hint = computed(() => {
            if (storedFraction.value === null) {
                return "No platform fee set. The pricing pipeline applies no platform fee.";
            }
            const pct = formatPercent(storedFraction.value);
            return `Current platform fee: ${pct}%. Applied to the cart total (excluding shipping).`;
        });

        const canSave = computed(() => {
            if (saving.value) return false;
            if (errorMsg.value) return false;
            if (percentDraft.value === "") {
                // Save with empty input clears the value -- only
                // valid when something was stored.
                return storedFraction.value !== null;
            }
            const n = Number(percentDraft.value);
            if (!Number.isFinite(n)) return false;
            if (n < 0 || n > 100) return false;
            return true;
        });

        async function load() {
            loading.value = true;
            errorMsg.value = "";
            try {
                const resp = await axios.get(URL);
                const value = resp?.data?.value ?? null;
                storedFraction.value = value;
                percentDraft.value = formatPercent(value);
                emit("rate-change", value);
            } catch (e) {
                errorMsg.value = "Could not load the platform fee.";
            } finally {
                loading.value = false;
            }
        }

        function onInput() {
            errorMsg.value = "";
            if (percentDraft.value === "") return;
            const n = Number(percentDraft.value);
            if (!Number.isFinite(n)) {
                errorMsg.value = "Enter a number between 0 and 100.";
                return;
            }
            if (n < 0 || n > 100) {
                errorMsg.value = "Platform fee must be between 0 and 100.";
            }
        }

        async function save() {
            saving.value = true;
            errorMsg.value = "";
            // Convert the entered PERCENT to the FRACTION the backend
            // stores (4% -> 0.04). Empty input clears the value (null).
            const fraction =
                percentDraft.value === ""
                    ? null
                    : Number(percentDraft.value) / 100;
            try {
                const resp = await axios.patch(URL, { value: fraction });
                const next = resp?.data?.value ?? null;
                storedFraction.value = next;
                percentDraft.value = formatPercent(next);
                emit("rate-change", next);
            } catch (e) {
                const status = e?.response?.status;
                if (status === 403) {
                    errorMsg.value =
                        "Permission denied. Only Super Admins can edit the platform fee.";
                } else if (status === 400) {
                    errorMsg.value =
                        "Invalid platform fee. Enter a percent between 0 and 100.";
                } else {
                    errorMsg.value = "Could not save the platform fee.";
                }
            } finally {
                saving.value = false;
            }
        }

        async function resetToCleared() {
            percentDraft.value = "";
            await save();
        }

        onMounted(load);

        return {
            loading,
            saving,
            errorMsg,
            hint,
            percentDraft,
            hasStoredValue,
            canSave,
            onInput,
            save,
            resetToCleared,
        };
    },
};
</script>

<style scoped>
.platform-fee-rate {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 16px 20px;
    margin-bottom: 24px;
}
.platform-fee-rate__header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 8px;
}
.platform-fee-rate__title {
    font-size: 18px;
    font-weight: 600;
    color: #1a202c;
    margin: 0;
}
.platform-fee-rate__live-badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 999px;
    background: #c6f6d5;
    color: #22543d;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}
.platform-fee-rate__lede {
    margin: 0 0 16px;
    color: #4b5563;
    max-width: 640px;
    line-height: 1.5;
    font-size: 14px;
}
.platform-fee-rate__form {
    max-width: 320px;
}
.platform-fee-rate__label {
    display: block;
    font-weight: 600;
    margin-bottom: 8px;
    color: #1f2937;
    font-size: 14px;
}
.platform-fee-rate__input-row {
    display: flex;
    align-items: center;
    gap: 8px;
}
.platform-fee-rate__input {
    flex: 1;
    padding: 8px 12px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 1rem;
    max-width: 120px;
}
.platform-fee-rate__suffix {
    color: #4b5563;
    font-size: 1rem;
}
.platform-fee-rate__hint {
    margin-top: 8px;
    color: #4b5563;
    font-size: 0.875rem;
}
.platform-fee-rate__error {
    margin-top: 8px;
    color: #b91c1c;
    font-size: 0.875rem;
}
.platform-fee-rate__actions {
    margin-top: 16px;
    display: flex;
    gap: 8px;
}
.tf-btn {
    border: 1px solid transparent;
    border-radius: 6px;
    padding: 8px 20px;
    font-size: 1rem;
    cursor: pointer;
}
.tf-btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}
.tf-btn--primary {
    background: #1f2937;
    color: #fff;
}
.tf-btn--ghost {
    background: transparent;
    color: #1f2937;
    border-color: #d1d5db;
}
</style>
